import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  CircularProgress, 
  Alert, 
  Grid, 
  Card, 
  CardContent, 
  CardActions,
  FormControlLabel,
  Checkbox,
  Slider,
  Chip
} from '@mui/material';
import { 
  ShoppingCart as ShoppingCartIcon,
  Refresh as RefreshIcon,
  Print as PrintIcon,
  LocalGroceryStore as GroceryIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import PredictionService from '../services/PredictionService';

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[4],
  },
}));

const CategoryChip = styled(Chip)(({ theme }) => ({
  fontWeight: 500,
  fontSize: '0.75rem',
  margin: theme.spacing(0.5),
}));

const ConfidenceIndicator = styled(Box)(({ theme, confidence }) => {
  let color;
  if (confidence >= 0.7) {
    color = theme.palette.success.main;
  } else if (confidence >= 0.4) {
    color = theme.palette.warning.main;
  } else {
    color = theme.palette.error.main;
  }
  
  return {
    display: 'flex',
    alignItems: 'center',
    color: color,
    fontSize: '0.75rem',
    fontWeight: 500,
    '& .MuiSvgIcon-root': {
      fontSize: '1rem',
      marginRight: theme.spacing(0.5),
    }
  };
});

const DaysIndicator = styled(Box)(({ theme, days }) => {
  let color;
  if (days > 5) {
    color = theme.palette.success.main;
  } else if (days > 2) {
    color = theme.palette.warning.main;
  } else {
    color = theme.palette.error.main;
  }
  
  return {
    display: 'flex',
    alignItems: 'center',
    color: color,
    fontWeight: 500,
    '& .MuiSvgIcon-root': {
      fontSize: '1rem',
      marginRight: theme.spacing(0.5),
    }
  };
});

function ShoppingListPage() {
  const [shoppingList, setShoppingList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [daysThreshold, setDaysThreshold] = useState(7);
  const [checkedItems, setCheckedItems] = useState({});
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    // Define fetchShoppingList inside useEffect to avoid dependency issues
    const fetchShoppingList = async () => {
      try {
        setIsLoading(true);
        setError('');
        
        const response = await PredictionService.getShoppingList(daysThreshold);
        
        if (response.success && response.shopping_list) {
          setShoppingList(response.shopping_list);
          
          // Initialize checked items state
          const initialCheckedState = {};
          response.shopping_list.forEach(item => {
            initialCheckedState[item.item_id] = false;
          });
          setCheckedItems(initialCheckedState);
        } else {
          setError('Failed to load shopping list');
        }
      } catch (error) {
        console.error('Error fetching shopping list:', error);
        setError('Failed to load shopping list. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchShoppingList();
  }, [daysThreshold, refreshTrigger]);

  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const handleDaysChange = (event, newValue) => {
    setDaysThreshold(newValue);
  };

  const handleCheckItem = (itemId) => {
    setCheckedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const handlePurchaseItems = async () => {
    try {
      setIsLoading(true);
      
      // Get all checked items
      const itemsToPurchase = Object.entries(checkedItems)
        .filter(([_, isChecked]) => isChecked)
        .map(([itemId]) => itemId);
      
      if (itemsToPurchase.length === 0) {
        alert('Please select at least one item to purchase');
        setIsLoading(false);
        return;
      }
      
      // Process each checked item
      for (const itemId of itemsToPurchase) {
        const item = shoppingList.find(item => item.item_id === itemId);
        if (item) {
          await PredictionService.recordPurchase(
            itemId,
            item.suggested_quantity,
            null,
            'Shopping List',
            'Purchased from smart shopping list'
          );
        }
      }
      
      // Reset checked items and refresh the list
      setCheckedItems({});
      handleRefresh();
      
      alert('Selected items marked as purchased!');
    } catch (error) {
      console.error('Error processing purchases:', error);
      setError('Failed to process purchases. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrintList = () => {
    const originalContents = document.body.innerHTML;
    
    const printStyles = `
      <style>
        body { font-family: Arial, sans-serif; }
        h1 { color: #3a7bd5; }
        .item { margin-bottom: 10px; padding: 5px; border-bottom: 1px solid #eee; }
        .category { color: #666; font-style: italic; }
        .quantity { font-weight: bold; }
      </style>
    `;
    
    let printHTML = '<html><head><title>Smart Shopping List</title>' + printStyles + '</head><body>';
    printHTML += '<h1>Smart Shopping List</h1>';
    printHTML += '<p>Generated on ' + new Date().toLocaleDateString() + '</p>';
    
    // Group items by category
    const categorizedItems = {};
    shoppingList.forEach(item => {
      if (!categorizedItems[item.category]) {
        categorizedItems[item.category] = [];
      }
      categorizedItems[item.category].push(item);
    });
    
    // Add items by category
    Object.entries(categorizedItems).forEach(([category, items]) => {
      printHTML += `<h2>${category}</h2>`;
      items.forEach(item => {
        printHTML += `
          <div class="item">
            <div>${item.item_name} ${item.brand ? `(${item.brand})` : ''}</div>
            <div class="quantity">${item.suggested_quantity} ${item.unit_of_measure}</div>
          </div>
        `;
      });
    });
    
    printHTML += '</body></html>';
    
    document.body.innerHTML = printHTML;
    window.print();
    document.body.innerHTML = originalContents;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3 }}
    >
      <Box sx={{ width: '100%' }}>
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 4,
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 0 }
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ShoppingCartIcon sx={{ mr: 1, color: 'primary.main', fontSize: '2rem' }} />
            <Typography variant="h4" component="h1" fontWeight="bold">
              Smart Shopping List
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={isLoading}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={handlePrintList}
              disabled={isLoading || shoppingList.length === 0}
            >
              Print
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<GroceryIcon />}
              onClick={handlePurchaseItems}
              disabled={isLoading || Object.values(checkedItems).every(v => !v)}
            >
              Mark Purchased
            </Button>
          </Box>
        </Box>

        {/* Days threshold slider */}
        <Paper elevation={2} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom>
            Prediction Timeframe
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Show items predicted to run out within the next {daysThreshold} days
          </Typography>
          <Box sx={{ px: 2 }}>
            <Slider
              value={daysThreshold}
              onChange={handleDaysChange}
              aria-labelledby="days-threshold-slider"
              valueLabelDisplay="auto"
              step={1}
              marks={[
                { value: 3, label: '3 days' },
                { value: 7, label: '1 week' },
                { value: 14, label: '2 weeks' },
                { value: 30, label: '1 month' }
              ]}
              min={1}
              max={30}
            />
          </Box>
        </Paper>

        {/* Shopping List Content */}
        <Box id="shopping-list-content">
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 4 }}>
              {error}
            </Alert>
          ) : shoppingList.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom>
                No items in your shopping list
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph>
                Either all your items are well-stocked, or you don't have enough consumption history yet for accurate predictions.
              </Typography>
              <Button 
                component={RouterLink} 
                to="/inventory" 
                variant="contained" 
                color="primary"
                sx={{ mt: 2 }}
              >
                View Inventory
              </Button>
            </Paper>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {shoppingList.length} {shoppingList.length === 1 ? 'item' : 'items'} in your shopping list
              </Typography>
              
              <Grid container spacing={3}>
                {shoppingList.map((item) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={item.item_id}>
                    <StyledCard>
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={!!checkedItems[item.item_id]}
                                onChange={() => handleCheckItem(item.item_id)}
                                color="primary"
                              />
                            }
                            label={
                              <Typography 
                                variant="h6" 
                                component="div" 
                                sx={{ 
                                  textDecoration: checkedItems[item.item_id] ? 'line-through' : 'none',
                                  color: checkedItems[item.item_id] ? 'text.disabled' : 'text.primary'
                                }}
                              >
                                {item.item_name}
                              </Typography>
                            }
                          />
                          
                          {item.is_low_stock && (
                            <Chip 
                              label="Low Stock" 
                              size="small" 
                              color="error" 
                              variant="outlined"
                            />
                          )}
                        </Box>
                        
                        <Box sx={{ mt: 1 }}>
                          <CategoryChip 
                            label={item.category} 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                          />
                          {item.brand && (
                            <CategoryChip 
                              label={item.brand} 
                              size="small" 
                              color="default" 
                              variant="outlined"
                            />
                          )}
                        </Box>
                        
                        <Box sx={{ mt: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            Current Stock:
                          </Typography>
                          <Typography variant="body1" fontWeight="medium">
                            {item.current_quantity} {item.unit_of_measure}
                          </Typography>
                        </Box>
                        
                        <Box sx={{ mt: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Suggested Purchase:
                          </Typography>
                          <Typography variant="body1" fontWeight="bold" color="primary.main">
                            {item.suggested_quantity} {item.unit_of_measure}
                          </Typography>
                        </Box>
                        
                        {item.days_until_depletion !== null && (
                          <Box sx={{ mt: 2 }}>
                            <DaysIndicator days={item.days_until_depletion}>
                              {item.days_until_depletion === 0 ? (
                                <span>Running out today!</span>
                              ) : item.days_until_depletion === 1 ? (
                                <span>Running out tomorrow!</span>
                              ) : (
                                <span>Running out in {item.days_until_depletion} days</span>
                              )}
                            </DaysIndicator>
                          </Box>
                        )}
                        
                        {item.confidence_level !== undefined && (
                          <Box sx={{ mt: 1 }}>
                            <ConfidenceIndicator confidence={item.confidence_level}>
                              Prediction confidence: {Math.round(item.confidence_level * 100)}%
                            </ConfidenceIndicator>
                          </Box>
                        )}
                      </CardContent>
                      
                      <CardActions sx={{ justifyContent: 'flex-end', p: 1 }}>
                        <Button
                          component={RouterLink}
                          to={`/inventory/${item.item_id}`}
                          size="small"
                          color="primary"
                        >
                          View Details
                        </Button>
                      </CardActions>
                    </StyledCard>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </Box>
      </Box>
    </motion.div>
  );
}

export default ShoppingListPage;